/**
 * SOLID Review
 * S — Strong. One use case: the consent platform reported a state — kept, and recorded on the server when it is new.
 * O — Strong. Whether a state is recorded, and at which version, is the Domain's decision.
 * L — Strong. Implements its use-case role exactly.
 * I — Strong. `execute`.
 * D — Strong. The decisions and legal-server ports and the session's step, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPayloadConsentState, ICommandConsentChanged, ICommandCookieConsentRecord } from '../../domain/ports/schemas/ConsentMoves';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import { DONE } from '../shared/ClientSteps';
import type { IConfigDepLegal } from './IConfigDepLegal';

/**
 * The CMP reported a state (FE4b; delivered on `ClientServerEvents`): the session keeps it; a resolved decision the server has
 * not heard of is recorded, at the Cookie Policy version the server publishes. The record is best-effort, as it was
 * (`.catch(() => undefined)`): consent works for the browser whether or not the audit landed.
 */
export class ConsentChangedUseCase implements IUseCase<IPayloadConsentState, TPortOutcome> {
  constructor(private readonly deps: IConfigDepLegal) {}

  async execute(ctx: IContextRequest, state: IPayloadConsentState): Promise<TPortOutcome> {
    const { decisions, step, legal } = this.deps;
    const changed: ICommandConsentChanged = { state };
    const kept = await step.run(ctx, (input) => decisions.consentChanged(ctx, input, changed));
    if (!kept.success) return kept;
    if (!kept.value.record) return DONE;
    const documents = await legal.documents(ctx);
    const published = documents.success ? documents.value.documents.map((d) => ({ type: d.type, version: d.version })) : [];
    const planned: ICommandCookieConsentRecord = { state, published };
    const record = await step.run(ctx, (input) => decisions.planCookieConsentRecord(ctx, input, planned));
    if (!record.success) return record;
    await legal.recordCookieConsent(ctx, record.value.request);
    return DONE;
  }
}
